import { useMemo, forwardRef, memo } from 'react';
import DayCell from './DayCell';
import EventBar from './EventBar';
import Milestone from './Milestone';
import { useSelectionContext } from '../contexts/SelectionContext';
import { useSidebarContext } from '../contexts/SidebarContext';
import { useDragContext } from '../contexts/DragContext';
import { useContextMenuContext } from '../contexts/ContextMenuContext';
import { toDateString } from '../utils/dateUtils';
import {
  CELL_MIN_HEIGHT,
  CELL_BOTTOM_PADDING,
  EVENT_BAR_TOP_OFFSET,
  EVENT_BAR_ROW_HEIGHT,
  MILESTONE_TOP_NO_EVENTS,
  MILESTONE_TOP_WITH_EVENTS_OFFSET,
  MILESTONE_HEIGHT
} from '../constants/layout';
import { ENTITY_TYPE } from '../constants/types';
import './MonthRow.scss';

/**
 * Renders a single month row in the calendar grid.
 * Displays day cells in a horizontal row with event bars and milestones
 * positioned as overlays. Handles dynamic row height based on stacked items.
 * 
 * @param {Object} props
 * @param {Object} props.month - Month data from generateYearData
 * @param {number} props.month.index - Month index (0-11)
 * @param {string} props.month.shortName - Abbreviated month name (e.g., 'Jan')
 * @param {Array} props.month.cells - Array of cell data for each day
 * @param {Array} [props.events=[]] - Events to display in this month
 * @param {Array} [props.milestones=[]] - Milestones to display in this month
 * @param {boolean} [props.hideLabel=false] - Whether to hide month label (when using sticky overlay)
 * @param {Object} [props.dragPreview=null] - Current drag preview state (passed from parent to enable memo optimization)
 * @param {Map} [props.holidayByDate=null] - Pre-computed holiday lookup map (date string -> holiday object)
 * @param {React.Ref} ref - Forwarded ref for measuring row height
 */
const MonthRow = forwardRef(function MonthRow({ 
  month, 
  events = [],
  milestones = [],
  hideLabel = false,
  dragPreview = null,
  holidayByDate: holidayByDateProp = null
}, ref) {
  // Get UI state from individual contexts (holidays now passed as prop for performance)
  const {
    isDateInSelection,
    onDayMouseDown,
    onDayMouseEnter,
    onDayMouseUp
  } = useSelectionContext();
  const {
    onEventClick,
    onMilestoneClick
  } = useSidebarContext();
  const {
    onDragStart,
    onDragEnd,
    onDrop
  } = useDragContext();
  const {
    onDayClick
  } = useContextMenuContext();
  // Create date-to-column index for O(1) lookups
  const dateToColumn = useMemo(() => {
    const map = new Map();
    month.cells.forEach((cell, index) => {
      if (cell.type === 'day' && cell.date) {
        const dateStr = toDateString(cell.date);
        map.set(dateStr, index);
      }
    });
    return map;
  }, [month.cells]);

  // Helper to get column range for a date range
  const getColumnRange = useMemo(() => {
    return (startDateStr, endDateStr) => {
      // Get month boundaries
      const monthStart = month.cells.find(c => c.type === 'day');
      const monthEnd = [...month.cells].reverse().find(c => c.type === 'day');
      if (!monthStart?.date || !monthEnd?.date) {
        return { startColumn: -1, endColumn: -1 };
      }
      
      const monthStartStr = toDateString(monthStart.date);
      const monthEndStr = toDateString(monthEnd.date);
      
      // Clamp dates to month boundaries
      const effectiveStart = startDateStr < monthStartStr ? monthStartStr : startDateStr;
      const effectiveEnd = endDateStr > monthEndStr ? monthEndStr : endDateStr;
      
      const startColumn = dateToColumn.get(effectiveStart) ?? -1;
      const endColumn = dateToColumn.get(effectiveEnd) ?? -1;
      
      return { startColumn, endColumn };
    };
  }, [month.cells, dateToColumn]);

  // Calculate event bars for this month
  const eventBars = useMemo(() => {
    const bars = [];
    const currentYear = month.cells.find(c => c.type === 'day')?.date?.getFullYear();
    const currentMonth = month.index + 1;
    if (!currentYear) return bars;
    
    events.forEach((event) => {
      // Use helper to get column range (O(1) instead of O(cells))
      const { startColumn, endColumn } = getColumnRange(event.startDate, event.endDate);
      if (startColumn === -1 || endColumn === -1) return;
      
      // Get the actual visible start date for this segment (clamped to month boundaries)
      const monthStart = month.cells.find(c => c.type === 'day');
      const monthEnd = [...month.cells].reverse().find(c => c.type === 'day');
      if (!monthStart?.date || !monthEnd?.date) return;
      
      // Find the next available row for this event's date range
      let row = 0;
      const usedRows = bars
        .filter(b => !(endColumn < b.startColumn || startColumn > b.endColumn))
        .map(b => b.row);
      
      while (usedRows.includes(row)) {
        row++;
      }
      
      // Determine continuation flags
      const [startYear, startMonth] = event.startDate.split('-').map(Number);
      const [endYear, endMonth] = event.endDate.split('-').map(Number);
      
      bars.push({
        event,
        startColumn,
        endColumn,
        row,
        continuesFromPrev: startYear < currentYear || (startYear === currentYear && startMonth < currentMonth),
        continuesToNext: endYear > currentYear || (endYear === currentYear && endMonth > currentMonth)
      });
    });
    
    return bars;
  }, [month, events, getColumnRange]);

  // Calculate milestones for this month with smart vertical positioning
  const monthMilestones = useMemo(() => {
    const result = [];
    
    milestones.forEach((milestone) => {
      // Parse the date string directly to avoid timezone issues
      // Format: YYYY-MM-DD (timezone-agnostic)
      const dateStr = milestone.date.substring(0, 10); // Get YYYY-MM-DD part
      const [, msMonth, msDay] = dateStr.split('-').map(Number);
      
      // Only process milestones that belong to this month
      // month.index is 0-based (0=Jan, 1=Feb, ..., 11=Dec)
      // msMonth is 1-based (1=Jan, 2=Feb, ..., 12=Dec)
      if (msMonth !== month.index + 1) {
        return;
      }
      
      // Find the day cell that matches this milestone
      for (let i = 0; i < month.cells.length; i++) {
        const cell = month.cells[i];
        if (cell.type === 'day' && cell.day === msDay) {
          // Find the highest row used by events on this day (column)
          const eventsOnThisDay = eventBars.filter(bar => 
            bar.startColumn <= i && bar.endColumn >= i
          );
          
          // Find milestones already placed on this day (column)
          const milestonesOnThisDay = result.filter(m => m.column === i);
          
          // Calculate the starting row for milestones
          // Smart positioning: find the first available slot (including gaps between events)
          let row = 0;
          
          if (eventsOnThisDay.length > 0 || milestonesOnThisDay.length > 0) {
            // Collect all occupied rows (by events and existing milestones)
            const occupiedRows = new Set([
              ...eventsOnThisDay.map(bar => bar.row),
              ...milestonesOnThisDay.map(m => m.row)
            ]);
            
            // Find the first available row (starting from 0)
            // This will use gaps between events instead of always going to the bottom
            while (occupiedRows.has(row)) {
              row++;
            }
          }
          
          result.push({
            milestone,
            column: i,
            row
          });
          break; // Found the match, stop searching
        }
      }
    });
    
    return result;
  }, [month, milestones, eventBars]);

  // Calculate required row height based on max event/milestone stack depth
  const requiredRowHeight = useMemo(() => {
    // Find max event row (highest stacked event)
    let maxEventRow = -1;
    if (eventBars.length > 0) {
      maxEventRow = Math.max(...eventBars.map(bar => bar.row));
    }
    
    // Find max milestone row
    let maxMilestoneRow = -1;
    if (monthMilestones.length > 0) {
      maxMilestoneRow = Math.max(...monthMilestones.map(m => m.row));
    }
    
    // Calculate height needed
    let neededHeight;
    
    // Determine the overall max row (events or milestones)
    const maxRow = Math.max(maxEventRow, maxMilestoneRow);
    
    if (maxRow >= 0) {
      // We have events and/or milestones
      // Calculate space needed for the highest row
      if (maxRow === maxEventRow && maxMilestoneRow < maxEventRow) {
        // Highest element is an event
        neededHeight = EVENT_BAR_TOP_OFFSET + (maxEventRow + 1) * EVENT_BAR_ROW_HEIGHT + CELL_BOTTOM_PADDING;
      } else {
        // Highest element is a milestone (or equal)
        neededHeight = maxMilestoneRow * EVENT_BAR_ROW_HEIGHT + MILESTONE_TOP_WITH_EVENTS_OFFSET + MILESTONE_HEIGHT + CELL_BOTTOM_PADDING;
      }
    } else {
      // Nothing, use minimum
      neededHeight = CELL_MIN_HEIGHT;
    }
    
    return Math.max(CELL_MIN_HEIGHT, neededHeight);
  }, [eventBars, monthMilestones]);

  // Calculate drag preview bar for this month (if applicable)
  const previewBar = useMemo(() => {
    if (!dragPreview) return null;
    
    const currentMonth = month.index + 1;
    const currentYear = month.cells.find(c => c.type === 'day')?.date?.getFullYear();
    if (!currentYear) return null;
    
    if (dragPreview.type === ENTITY_TYPE.EVENT && dragPreview.startDate && dragPreview.endDate) {
      // Use the original event from dragPreview (handles cross-month dragging)
      const originalEvent = dragPreview.originalItem;
      if (!originalEvent) return null;
      
      // Use helper to get column range (O(1) instead of O(cells))
      const { startColumn, endColumn } = getColumnRange(dragPreview.startDate, dragPreview.endDate);
      if (startColumn === -1 || endColumn === -1) return null;
      
      // Determine continuation flags
      const [startYear, startMonth] = dragPreview.startDate.split('-').map(Number);
      const [endYear, endMonth] = dragPreview.endDate.split('-').map(Number);
      
      return {
        event: { ...originalEvent, id: `preview-${originalEvent.id}` },
        startColumn,
        endColumn,
        row: 0, // Preview always on top
        continuesFromPrev: startYear < currentYear || (startYear === currentYear && startMonth < currentMonth),
        continuesToNext: endYear > currentYear || (endYear === currentYear && endMonth > currentMonth),
        isPreview: true
      };
    }
    
    if (dragPreview.type === ENTITY_TYPE.MILESTONE && dragPreview.date) {
      // Use the original milestone from dragPreview (handles cross-month dragging)
      const originalMilestone = dragPreview.originalItem;
      if (!originalMilestone) return null;
      
      const [msYear, msMonth] = dragPreview.date.split('-').map(Number);
      
      // Check if preview is in this month
      if (msMonth !== currentMonth || msYear !== currentYear) return null;
      
      // Find the column using dateToColumn map (O(1))
      const column = dateToColumn.get(dragPreview.date);
      if (column === undefined) return null;
      
      return {
        milestone: { ...originalMilestone, id: `preview-${originalMilestone.id}` },
        column,
        row: 0, // Preview always on top, consistent with event preview
        isPreview: true
      };
    }
    
    return null;
  }, [dragPreview, month, getColumnRange, dateToColumn]);

  // Use pre-computed holiday map from parent (or empty map if not provided)
  const holidayByDate = holidayByDateProp || new Map();

  return (
    <div className="month-row" ref={ref}>
      {/* 
        Label is hidden when sticky overlays are used, but kept in DOM
        to maintain the grid layout structure. The sticky overlay
        (rendered in Calendar.jsx) displays the visible label instead.
      */}
      <div 
        className={`month-row__label ${hideLabel ? 'month-row__label--hidden' : ''}`}
        style={{ height: `${requiredRowHeight}px` }}
      >
        <span className="month-row__name">{month.shortName}</span>
      </div>
      <div className="month-row__cells">
        {month.cells.map((cell, index) => {
          // Find holiday for this day cell using O(1) lookup
          const holiday = cell.date ? holidayByDate.get(toDateString(cell.date)) : null;
          
          return (
            <DayCell 
              key={index} 
              cell={cell}
              height={requiredRowHeight}
              isSelected={cell.date && isDateInSelection ? isDateInSelection(cell.date) : false}
              holiday={holiday}
              onMouseDown={onDayMouseDown}
              onMouseEnter={onDayMouseEnter}
              onMouseUp={onDayMouseUp}
              onClick={onDayClick}
              onDrop={onDrop}
            />
          );
        })}
        {/* Event bars overlay */}
        {eventBars.map((bar, index) => {
          // Hide the original event if it's being dragged
          const isBeingDragged = dragPreview?.type === ENTITY_TYPE.EVENT && dragPreview?.id === bar.event.id;
          return (
            <EventBar
              key={`${bar.event.id}-${index}`}
              event={bar.event}
              startColumn={bar.startColumn}
              endColumn={bar.endColumn}
              row={bar.row}
              continuesFromPrev={bar.continuesFromPrev}
              continuesToNext={bar.continuesToNext}
              isHidden={isBeingDragged}
              onClick={onEventClick}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          );
        })}
        {/* Preview event bar (shown during drag) */}
        {previewBar && previewBar.event && (
          <EventBar
            key={`preview-${previewBar.event.id}`}
            event={previewBar.event}
            startColumn={previewBar.startColumn}
            endColumn={previewBar.endColumn}
            row={previewBar.row}
            continuesFromPrev={previewBar.continuesFromPrev}
            continuesToNext={previewBar.continuesToNext}
            isPreview={true}
          />
        )}
        {/* Milestones overlay */}
        {monthMilestones.map((item, index) => {
          // Hide the original milestone if it's being dragged
          const isBeingDragged = dragPreview?.type === ENTITY_TYPE.MILESTONE && dragPreview?.id === item.milestone.id;
          return (
            <Milestone
              key={`${item.milestone.id}-${index}`}
              milestone={item.milestone}
              column={item.column}
              row={item.row}
              isHidden={isBeingDragged}
              onClick={onMilestoneClick}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          );
        })}
        {/* Preview milestone (shown during drag) */}
        {previewBar && previewBar.milestone && (
          <Milestone
            key={`preview-${previewBar.milestone.id}`}
            milestone={previewBar.milestone}
            column={previewBar.column}
            row={previewBar.row}
            isPreview={true}
          />
        )}
      </div>
    </div>
  );
});

/**
 * Custom comparison function for React.memo.
 * Only re-render if month data, events, milestones, hideLabel, or relevant dragPreview changes.
 * This prevents unnecessary re-renders when drag preview is in other months.
 */
function arePropsEqual(prevProps, nextProps) {
  // Always re-render if core props changed
  if (
    prevProps.month !== nextProps.month ||
    prevProps.hideLabel !== nextProps.hideLabel
  ) {
    return false;
  }
  
  // Check if events array changed (shallow comparison)
  if (prevProps.events.length !== nextProps.events.length) {
    return false;
  }
  for (let i = 0; i < prevProps.events.length; i++) {
    if (prevProps.events[i] !== nextProps.events[i]) {
      return false;
    }
  }
  
  // Check if milestones array changed (shallow comparison)
  if (prevProps.milestones.length !== nextProps.milestones.length) {
    return false;
  }
  for (let i = 0; i < prevProps.milestones.length; i++) {
    if (prevProps.milestones[i] !== nextProps.milestones[i]) {
      return false;
    }
  }
  
  // Smart dragPreview comparison: only re-render if dragPreview affects this month
  const prevPreview = prevProps.dragPreview;
  const nextPreview = nextProps.dragPreview;
  
  // If both null/undefined, no change
  if (!prevPreview && !nextPreview) {
    return true;
  }
  
  // If one is null and the other isn't, need to re-render
  if (!prevPreview || !nextPreview) {
    // But only if the non-null preview is actually in this month
    const preview = prevPreview || nextPreview;
    const monthIndex = nextProps.month.index;
    
    // Check if preview is in this month
    if (preview.type === 'event' && preview.startDate && preview.endDate) {
      const [, startMonth] = preview.startDate.split('-').map(Number);
      const [, endMonth] = preview.endDate.split('-').map(Number);
      if (startMonth - 1 <= monthIndex && endMonth - 1 >= monthIndex) {
        return false; // Preview affects this month, re-render
      }
    } else if (preview.type === 'milestone' && preview.date) {
      const [, month] = preview.date.split('-').map(Number);
      if (month - 1 === monthIndex) {
        return false; // Preview affects this month, re-render
      }
    }
    return true; // Preview doesn't affect this month, skip re-render
  }
  
  // Both exist - check if they're different AND affect this month
  if (prevPreview === nextPreview) {
    return true; // Same object reference
  }
  
  // Different preview objects - re-render (conservative approach)
  return false;
}

export default memo(MonthRow, arePropsEqual);
